import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { SiteVideoPublic } from "@tih/shared";
import { ApiError, fetchVideos } from "../api/client";
import { useReveal } from "../hooks/useReveal";

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function VideosPage() {
  const ref = useReveal<HTMLElement>();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category")?.trim() || "";

  const [videos, setVideos] = useState<SiteVideoPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchVideos(categoryFilter ? { category: categoryFilter } : undefined)
      .then((data) => {
        if (!cancelled) setVideos(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : String((e as Error).message ?? e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categoryFilter]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const v of videos) {
      if (v.category?.trim()) set.add(v.category.trim());
    }
    // When filtering, we only have that category's videos — still show the active chip.
    if (categoryFilter) set.add(categoryFilter);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [videos, categoryFilter]);

  function setCategory(next: string) {
    if (!next) {
      setSearchParams({});
    } else {
      setSearchParams({ category: next });
    }
  }

  return (
    <section className="section section-videos" id="videos" ref={ref}>
      <div className="videos-head">
        <div className="sec-eyebrow">
          <em>Watch</em>
        </div>
        <h2 className="sec-title">Videos</h2>
        <p className="videos-lead">
          Worship nights and moments from Together In Heaven — watch on this site, without leaving
          for YouTube.
        </p>
      </div>

      {categories.length > 0 && (
        <div className="videos-filters" role="tablist" aria-label="Filter by category">
          <button
            type="button"
            className={`videos-filter-chip${!categoryFilter ? " is-active" : ""}`}
            onClick={() => setCategory("")}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              className={`videos-filter-chip${categoryFilter === c ? " is-active" : ""}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="page-empty" aria-busy="true">
          Loading videos…
        </div>
      ) : error ? (
        <div className="page-empty page-empty--error" role="alert">
          {error}
        </div>
      ) : videos.length === 0 ? (
        <div className="page-empty">
          <p className="page-empty-title">Videos will appear here</p>
          <p>
            Published worship recordings will show up in this gallery. Check back after the next
            night, or see <Link to="/event">the upcoming event</Link>.
          </p>
        </div>
      ) : (
        <div className="videos-grid">
          {videos.map((v) => (
            <Link
              key={v.videoId}
              to={`/videos/view/${encodeURIComponent(v.videoId)}`}
              className="video-card"
            >
              <div className="video-card-thumb">
                {v.thumbnailUrl ? (
                  <img src={v.thumbnailUrl} alt="" loading="lazy" />
                ) : (
                  <div className="photo-ph" data-label={v.title} />
                )}
                {v.liveBroadcast && <span className="video-card-badge">Live</span>}
              </div>
              <div className="video-card-meta">
                <h3>{v.title}</h3>
                {v.publishedAt && (
                  <time dateTime={v.publishedAt}>{formatWhen(v.publishedAt)}</time>
                )}
                {v.category && <span className="video-card-category">{v.category}</span>}
                {v.description && <p>{v.description}</p>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
